import BaseRecordRenderer from "@reusables/BaseRecordRenderer";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import ViewLayout from "../components/ViewLayout";

import { Alert } from "@mui/material";

import { Can, useAbility } from "@/casl.config";
import NoPermissionBanner from "@/components/ErrorPages/NoPermissionBanner";
import { BadgeStatus } from "@/types/general";
import ReservationsPresenceBadge from "@components/Dashboard/pages/Sales/Orders/components/ReservationsPresenceBadge";
import { downloadBlobExport, genT, toastError } from "@helpers/utils";
import { useExportOrderMutation } from "@redux/api/internalApiSlice";
import { isSystemIntegratedSelector } from "@redux/features/auth/authSlice";
import { INVOICABLE_INTEGRATIONS } from "@redux/features/integrations/integrationsApi";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import {
    useConfirmSaleOrderMutation,
    useGetSaleOrderQuery,
    useInvoiceSaleOrderMutation,
} from "@redux/features/sales/orders/salesOrdersApi";
import { handleSaleOrderCopy } from "@redux/features/sales/orders/thunks";
import BaseOrderStatusBadge from "@reusables/BaseOrderStatusBadge";
import BasePermissionBlocker from "@reusables/BasePermissionBlocker";
import { BaseTinyActionIcon, TinyActionIconType } from "@reusables/BaseTinyActionIcon";
import BaseTooltip from "@reusables/BaseTooltip";
import UIButton from "@reusables/UIButton";
import { toast } from "react-toastify";
import IntegrationsBadge from "../components/IntegrationsBadge";
import OrderCancellationModal from "../components/modals/CancellationOrderModal";
import OrderDeletionModal from "../components/modals/DeleteOrder";
import OfferActivationModal from "../components/modals/OfferActivationModal";
import PrepaidInvoiceModal from "../components/modals/PrepaidInvoiceModal";
import { CancelOrderAction } from "../utils";

export default function SaleOrderDetailsPage() {
    const history = useHistory();
    const { t } = useTranslation("", { keyPrefix: "sales.orders.mutations.details" });

    const ability = useAbility();
    const dispatch = useAppDispatch();
    const hasInvoicableIntegration = useAppSelector((state) =>
        INVOICABLE_INTEGRATIONS.some((integration) =>
            isSystemIntegratedSelector(integration)(state)
        )
    );

    const { id } = useParams<{ id: string }>();

    const {
        data: saleOrder,
        isLoading: isSaleOrderLoading,
        refetch,
    } = useGetSaleOrderQuery(parseInt(id));

    const [isDeletionModalOpen, setIsDeletionModalOpen] = React.useState<boolean>(false);
    const [isCancellationModalOpen, setIsCancellationModalOpen] = React.useState<boolean>(false);
    const [isOfferActivationModalOpen, setIsOfferActivationModalOpen] =
        React.useState<boolean>(false);
    const [isPrepaidInvoiceModalOpen, setIsPrepaidInvoiceModalOpen] =
        React.useState<boolean>(false);

    const [exportPDF, { isLoading: isExportLoading }] = useExportOrderMutation();
    const [confirmSaleOrder, { isLoading: isOfferConfirmationLoading }] =
        useConfirmSaleOrderMutation();
    const [invoiceSaleOrder, { isLoading: isPrepaidInvoiceLoading }] =
        useInvoiceSaleOrderMutation();

    // Deriving order state once and reusing it across banner/actions gating.
    const isOfferStatus = saleOrder?.status.name === BadgeStatus.Base.offer;
    const isPrepaidStatus = saleOrder?.status.name === BadgeStatus.Base.prepaid;
    const isPrepaidAwaitingInvoice = isPrepaidStatus && !saleOrder?.is_invoiced;
    const shouldShowPrepaidBanner = isPrepaidAwaitingInvoice && hasInvoicableIntegration;
    const isPickingDisabled = isOfferStatus || isPrepaidAwaitingInvoice;
    const pickingTooltip = isOfferStatus
        ? `${t("actions.pickingNotAllowed.offer")}`
        : isPrepaidAwaitingInvoice
        ? `${t("actions.pickingNotAllowed.prepaid")}`
        : genT("general.actions.startPicking");

    return (
        <>
            <Can not I="view" a="sale_order">
                <div className="levitation-extended">
                    <NoPermissionBanner />
                </div>
            </Can>

            <Can I="view" a="sale_order">
                <BaseRecordRenderer
                    record={saleOrder}
                    loading={isSaleOrderLoading || isExportLoading}
                    hideLevitation
                >
                    <div className="space-y-[40px] mb-[24px] levitation-extended">
                        <div className="flex justify-between">
                            <div className="flex space-x-2 items-center">
                                <h6 className="text-accent grow m-auto">
                                    {t("heading")} #{saleOrder?.code ?? saleOrder?.id}
                                </h6>
                                {saleOrder?.status.name && (
                                    <BaseOrderStatusBadge status={saleOrder.status.name} />
                                )}
                                <ReservationsPresenceBadge
                                    has_reservations={saleOrder?.has_reservations ?? false}
                                />
                                <IntegrationsBadge row={saleOrder} width={24} height={24} />
                                <div id={"reference_number"}></div>
                            </div>
                            <div className="space-x-[8px] flex items-center h-full">
                                <BaseTinyActionIcon
                                    type={TinyActionIconType.PDF}
                                    tooltip={genT("general.actions.pdf")}
                                    onClick={() =>
                                        exportPDF({ id: saleOrder?.id as number, type: "sale" })
                                            .unwrap()
                                            .then((data) => {
                                                downloadBlobExport(
                                                    () => data,
                                                    `sales_order_${
                                                        saleOrder?.code ?? saleOrder?.id
                                                    }`,
                                                    "pdf"
                                                );
                                            })
                                    }
                                />
                                <BasePermissionBlocker action="create" module="picking">
                                    <BaseTooltip
                                        active
                                        title={pickingTooltip}
                                        placement="bottom"
                                    >
                                        <BaseTinyActionIcon
                                            type={TinyActionIconType.StartPicking}
                                            // Blocking picking navigation while order is still in Offer.
                                            onClick={() => {
                                                if (
                                                    !isPickingDisabled &&
                                                    ability.can("create", "picking")
                                                ) {
                                                    void history.push(`/dashboard/picking/new`, {
                                                        saleOrder: saleOrder,
                                                    });
                                                }
                                            }}
                                            className={
                                                isPickingDisabled
                                                    ? "opacity-20 cursor-not-allowed"
                                                    : ""
                                            }
                                        />
                                    </BaseTooltip>
                                </BasePermissionBlocker>
                                <BasePermissionBlocker action="create" module="sale_order">
                                    <BaseTinyActionIcon
                                        type={TinyActionIconType.Duplicate}
                                        tooltip={genT("general.actions.duplicate")}
                                        onClick={() => {
                                            if (saleOrder?.id) {
                                                void dispatch(
                                                    handleSaleOrderCopy(saleOrder.id, history)
                                                );
                                            }
                                        }}
                                    />
                                </BasePermissionBlocker>
                                <BasePermissionBlocker action="edit" module="sale_order">
                                    <BaseTooltip
                                        active={ability.can("edit", "sale_order")}
                                        title={genT("general.actions.edit")}
                                        placement="bottom"
                                    >
                                        <BaseTinyActionIcon
                                            type={TinyActionIconType.Edit}
                                            onClick={() =>
                                                ability.can("edit", "sale_order") &&
                                                history.push(
                                                    `/dashboard/sales/orders/${saleOrder?.id}/edit`
                                                )
                                            }
                                        />
                                    </BaseTooltip>
                                </BasePermissionBlocker>
                                <BasePermissionBlocker action="delete" module="sale_order">
                                    <BaseTooltip
                                        active={ability.can("delete", "sale_order")}
                                        title={genT("general.actions.delete")}
                                        placement="bottom"
                                    >
                                        <BaseTinyActionIcon
                                            type={TinyActionIconType.Delete}
                                            onClick={() =>
                                                ability.can("delete", "sale_order") &&
                                                setIsDeletionModalOpen(true)
                                            }
                                        />
                                    </BaseTooltip>
                                </BasePermissionBlocker>
                                <BasePermissionBlocker action="cancel" module="sale_order">
                                    <CancelOrderAction
                                        actionEntity={saleOrder}
                                        action="cancel"
                                        module="sale_order"
                                        t={t}
                                        onClick={() => setIsCancellationModalOpen(true)}
                                        tinyActionIconVariant="bordered"
                                    />
                                </BasePermissionBlocker>
                            </div>
                        </div>
                        {isOfferStatus && (
                            <Alert
                                severity="warning"
                                sx={{
                                    backgroundColor: "#FCF8E9",
                                    borderRadius: "8px",
                                    border: "1px solid #FCF8E9",
                                    "& .MuiAlert-icon": {
                                        color: "#D9B153",
                                        alignSelf: "center",
                                    },
                                    "& .MuiAlert-message": {
                                        width: "100%",
                                    },
                                }}
                            >
                                {/* Rendering Offer activation entrypoint directly on details header. */}
                                <div className="w-full flex items-center justify-between gap-[16px] flex-wrap">
                                    <div className="flex items-center gap-[8px] flex-wrap">
                                        <span className="text-accent font-normal">
                                            {t("offerBanner.inactive")}.
                                        </span>
                                        <span className="text-accent font-light">
                                            {t("offerBanner.hint")}.
                                        </span>
                                    </div>
                                    <UIButton
                                        type="button"
                                        size="md"
                                        onClick={() => setIsOfferActivationModalOpen(true)}
                                    >
                                        <span>{t("offerBanner.buttons.activate")}</span>
                                    </UIButton>
                                </div>
                            </Alert>
                        )}
                        {shouldShowPrepaidBanner && (
                            <Alert
                                severity="warning"
                                sx={{
                                    backgroundColor: "#FCF8E9",
                                    borderRadius: "8px",
                                    border: "1px solid #FCF8E9",
                                    "& .MuiAlert-icon": {
                                        color: "#D9B153",
                                        alignSelf: "center",
                                    },
                                    "& .MuiAlert-message": {
                                        width: "100%",
                                    },
                                }}
                            >
                                <div className="w-full flex items-center justify-between gap-[16px] flex-wrap">
                                    <div className="flex items-center gap-[8px] flex-wrap">
                                        <span className="text-accent font-normal">
                                            {t("prepaidBanner.notInvoiced")}.
                                        </span>
                                        <span className="text-accent font-light">
                                            {t("prepaidBanner.hint")}.
                                        </span>
                                    </div>
                                    <BasePermissionBlocker action="edit" module="sale_order">
                                        <UIButton
                                            type="button"
                                            size="md"
                                            onClick={() => setIsPrepaidInvoiceModalOpen(true)}
                                        >
                                            <span>{t("prepaidBanner.buttons.send")}</span>
                                        </UIButton>
                                    </BasePermissionBlocker>
                                </div>
                            </Alert>
                        )}
                        <ViewLayout.Main saleOrder={saleOrder as NonNullable<typeof saleOrder>} />
                    </div>

                    <ViewLayout.Pickings saleOrder={saleOrder as NonNullable<typeof saleOrder>} />
                </BaseRecordRenderer>
            </Can>

            <OrderDeletionModal
                isOpen={isDeletionModalOpen}
                entity_id={parseInt(id)}
                onClose={() => setIsDeletionModalOpen(false)}
                onDeletion={() => history.push("/dashboard/sales/orders")}
            />
            <OrderCancellationModal
                isOpen={isCancellationModalOpen}
                entityId={parseInt(id)}
                onClose={() => setIsCancellationModalOpen(false)}
                onCancellation={() => history.push("/dashboard/sales/orders")}
                showInvoicedPrepaidWarning={isPrepaidStatus && !!saleOrder?.is_invoiced}
            />
            <OfferActivationModal
                isOpen={isOfferActivationModalOpen}
                isLoading={isOfferConfirmationLoading}
                onClose={() => setIsOfferActivationModalOpen(false)}
                customer={saleOrder?.customer?.name}
                paymentTerms={saleOrder?.payment_terms?.name}
                deliveryTerms={saleOrder?.delivery_terms?.name}
                onConfirm={() => {
                    if (!saleOrder) return;

                    // Confirming Offer -> New and refetching details to sync UI state immediately.
                    confirmSaleOrder({ id: saleOrder.id })
                        .unwrap()
                        .then(() => {
                            toast.success(t("offerBanner.responses.success"));
                            void refetch();
                        })
                        .catch((e) => {
                            toastError(e);
                        })
                        .finally(() => setIsOfferActivationModalOpen(false));
                }}
            />
            <PrepaidInvoiceModal
                isOpen={isPrepaidInvoiceModalOpen}
                isLoading={isPrepaidInvoiceLoading}
                onClose={() => setIsPrepaidInvoiceModalOpen(false)}
                customer={saleOrder?.customer?.name}
                paymentTerms={saleOrder?.payment_terms?.name}
                deliveryTerms={saleOrder?.delivery_terms?.name}
                onConfirm={() => {
                    if (!saleOrder) return;

                    invoiceSaleOrder({ id: saleOrder.id })
                        .unwrap()
                        .then(() => {
                            toast.success(t("prepaidBanner.responses.success"));
                            void refetch();
                        })
                        .catch((e) => {
                            toastError(e);
                        })
                        .finally(() => setIsPrepaidInvoiceModalOpen(false));
                }}
            />
        </>
    );
}
